import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { Form990Data } from '../types/reporting';

// Create context with default values
interface Form990ContextType {
  savedResponses: any;
  saveResponses: (responses: any) => Promise<void>;
  generatePreview: (responses: any) => Promise<any>;
  form990Data: Form990Data | null;
  isLoading: boolean;
  error: string | null;
  regenerate990: () => Promise<void>;
}

const Form990Context = createContext<Form990ContextType | undefined>(undefined);

// Provider component
export const Form990Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [savedResponses, setSavedResponses] = useState<any>(null);
  const [form990Data, setForm990Data] = useState<Form990Data | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Save questionnaire responses
  const saveResponses = useCallback(async (responses: any) => {
    try {
      await axios.post('/api/form990/responses', responses);
      setSavedResponses(responses);
    } catch (error) {
      console.error('Error saving responses:', error);
    }
  }, []);

  // Generate Form 990 preview
  const generatePreview = useCallback(async (responses: any) => {
    try {
      const preview = await axios.post('/api/form990/preview', responses);
      return preview.data;
    } catch (error) {
      console.error('Error generating preview:', error);
      return null;
    }
  }, []);

  // Fetch Form 990 data
  const fetchForm990Data = useCallback(async (fiscalSponsorId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/api/fiscal-sponsors/${fiscalSponsorId}/form990-data`);
      setForm990Data(response.data);
    } catch (error: any) {
      setError(error.message || 'Failed to fetch Form 990 data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Regenerate Form 990
  const regenerate990 = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.post('/api/form990/regenerate');
      setForm990Data(response.data);
    } catch (error: any) {
      setError(error.message || 'Failed to regenerate Form 990');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <Form990Context.Provider 
      value={{ 
        savedResponses, 
        saveResponses, 
        generatePreview,
        form990Data,
        isLoading,
        error,
        regenerate990
      }}
    >
      {children}
    </Form990Context.Provider>
  );
};

// Export the hook for using the context
export const useForm990Context = () => {
  const context = useContext(Form990Context);
  if (context === undefined) {
    throw new Error('useForm990Context must be used within a Form990Provider');
  }
  return context;
};

// For backward compatibility
export const useForm990 = useForm990Context; 